import {TableContainer, TableHead, TableBody, TableCell, TableRow, Paper, Table, Grid, Tabs, Tab} from '@mui/material';
import {useTranslation} from "react-i18next";
import {observer} from 'mobx-react';
import React from 'react';
import {monthNames, getCityName, getCountryName} from '../Trans.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import {TAB_SPEC, TAB_LIFER} from './StatsCommon.js';
import StatsFilter from './StatsFilter.js';
import StatsRow from './StatsRow.js';
import {COMM_OK} from '../WsR2.js';

const LT_LIST		= 0;
const LT_BY_MONTH	= 1;
const LT_BY_CITY	= 2;

const LiferList = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	function lifeSrc() {
		let prev_y = -1, i;
		let ret = [];

		if (!gStorage.stats.obsList || gStorage.stats.spec_tab !== TAB_LIFER)
			return null;

		let ol = gStorage.stats.obsList.length;

		for (i = ol - 1; i >= 0; i--) {
			let obs = gStorage.stats.obsList[i];
			const y = new Date(obs.start_t * 1000.0).getYear();

			if (y !== prev_y) {
				let j, n;

				n = 1;

				for (j = i - 1; j >= 0; j--) {
					let obs2 = gStorage.stats.obsList[j];
					const y2 = new Date(obs2.start_t * 1000.0).getYear();

					if (y2 !== y) 
						break;

					n++;
				}

				if (prev_y !== -1)
					ret.push(<TableRow key={-y * 2 - 1}><TableCell colSpan={4}>&nbsp;</TableCell></TableRow>);

				ret.push(<TableRow key={-y * 2} style={{backgroundColor: '#f0f0f0'}}><TableCell colSpan={4}>{(y + 1900) + ' ' + n + ' ' + t('stats.species')}</TableCell></TableRow>);
			}

			ret.push(
				<StatsRow
					key = {i + 1}
					bgcolor = '#ffffff'
					row_n = {i + 1}
					obs = {obs}
				/>);

			prev_y = y;
		}

		return ret;
	}

	if (!gStorage.stats.obsList || gStorage.stats.lifer_tab !== LT_LIST)
		return null;

	return 	(<TableContainer component={Paper} elevation={0}>
			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell>#</TableCell>
						<TableCell>{t('main.species')}</TableCell>
						<TableCell>{t('main.time')}</TableCell>
						<TableCell>{t('main.location')}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{lifeSrc()}
				</TableBody>
			</Table>
		</TableContainer>
	);
})

const LSO_CITY		= 0;
const LSO_COUNT		= 1;

const LSD_ASC		= 0;
const LSD_DESC		= 1;

const LiferByCity = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	let tot_num = 0, tot_cnt = 0;

	function citySums() {
		let counts = [];

		gStorage.stats.obsList.forEach((obs) => {
			const found = counts.find((e) => e.city_id === obs.city_id);
			if (!found) {
				counts.push({city_id: obs.city_id, cnt: 1});
			} else {
				found.cnt++;
			}
		})

		return counts;
	}

	function statsLiferGetCityName(id) {
		const city = gStorage.lists.citylist.list.find(elem => elem.id === id);
		const usr = gStorage.conn.user;

		// console.log('statsCityGetCityName', id, city);

		let a_str = '';

		if (city.country !== usr.def_country) {
			const country = gStorage.lists.countrylist.find(x => x.id === city.country);
			a_str = getCountryName(country, usr) + ', ';
		}

		return a_str += getCityName(city, gStorage.conn.user);
	}

	function citySumSortFunction(p1, p2) {
		if (gStorage.stats.lifer_sort_order === LSO_CITY) {
			if (gStorage.stats.lifer_sort_dir === LSD_DESC)
				return statsLiferGetCityName(p2.city_id).localeCompare(statsLiferGetCityName(p1.city_id), 'fi');

			return statsLiferGetCityName(p1.city_id).localeCompare(statsLiferGetCityName(p2.city_id), 'fi');
		}

		if (gStorage.stats.lifer_sort_dir === LSD_DESC)
			return p2.cnt - p1.cnt;

		return p1.cnt - p2.cnt;
	}

	function citySignCityName() {
		if (gStorage.stats.lifer_sort_order !== LSO_CITY)
			return '';

		return gStorage.stats.lifer_sort_dir === LSD_DESC ? ' ↓' : ' ↑' ;
	}
	
	function citySignCount() {
		if (gStorage.stats.lifer_sort_order !== LSO_COUNT)
			return '';

		return gStorage.stats.lifer_sort_dir === LSD_DESC ? ' ↓' : ' ↑' ;
	}

	function tglSortDir() {
		return gStorage.stats.lifer_sort_dir === LSD_DESC ? LSD_ASC : LSD_DESC;
	}

	function setSortByCityName() {
		if (gStorage.stats.lifer_sort_order === LSO_CITY) {
			gStorage.stats.setLiferOrder(LSO_CITY, tglSortDir());
		} else {
			gStorage.stats.setLiferOrder(LSO_CITY, LSD_ASC);
		}
	}

	function setSortByCityCount() {
		if (gStorage.stats.lifer_sort_order === LSO_COUNT) {
			gStorage.stats.setLiferOrder(LSO_COUNT, tglSortDir());
		} else {
			gStorage.stats.setLiferOrder(LSO_COUNT, LSD_DESC);
		}
	}

	if (!gStorage.stats.obsList || gStorage.stats.lifer_tab !== LT_BY_CITY)
		return null;

	return 	(
		<Grid size={{xs: 12}} align={'center'}>
			<TableContainer component={Paper} elevation={0} style={{width: 300, margin: 2}}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell>#</TableCell>
							<TableCell sx={{cursor: 'pointer'}} onClick={setSortByCityName}>{t('stats.city') + citySignCityName()}</TableCell>
							<TableCell sx={{cursor: 'pointer', textAlign: 'right'}} onClick={setSortByCityCount}>{t('stats.count') + citySignCount()}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{citySums().sort(citySumSortFunction).map((cs, ind) => {
							tot_num += cs.cnt;
							tot_cnt++;

							return (
								<TableRow key={ind}>
									<TableCell>
										{ind + 1}
									</TableCell>
									<TableCell>
										{statsLiferGetCityName(cs.city_id)}
									</TableCell>
									<TableCell style={{textAlign: 'right'}}>
										{cs.cnt}
									</TableCell>
								</TableRow>
							);
						})}

						<TableRow key={tot_cnt}>
							<TableCell>
							</TableCell>
							<TableCell>
							</TableCell>
							<TableCell style={{textAlign: 'right', fontWeight: 'bold'}}>
								{tot_num}
							</TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>
		</Grid>
	);
});

const LiferByMonth = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	let tot_num = 0;

	const monthlySums = () => {
		let counts = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

		gStorage.stats.obsList.forEach((obs) => {
			const m = new Date(obs.start_t * 1000.0).getMonth();
			counts[m]++;
		})

		return counts;
	}

	if (!gStorage.stats.obsList || gStorage.stats.lifer_tab !== LT_BY_MONTH)
		return null;

	return 	(
		<Grid size={{xs: 12}} align={'center'}>
			<TableContainer component={Paper} elevation={0} style={{width: 300, margin: 2}}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell>{t('stats.month')}</TableCell>
							<TableCell style={{textAlign: 'right'}}>{t('stats.count')}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{monthlySums().map((ms, ind) => {
							tot_num += ms;

							return (
								<TableRow key={ind}>
									<TableCell>
										{t(monthNames[ind])}
									</TableCell>
									<TableCell style={{textAlign: 'right'}}>
										{ms}
									</TableCell>
								</TableRow>
							);
						})}

						<TableRow key={12}>
							<TableCell>
							</TableCell>
							<TableCell style={{textAlign: 'right', fontWeight: 'bold'}}>
								{tot_num}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</Grid>
	);
});

const StatsLifer = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	function liferAddLeftEnabled() {
		return gStorage.stats.obsList && gStorage.stats.obsList.length !== 0 &&
			(gStorage.stats.rightList.tab !== TAB_LIFER ||
				gStorage.stats.rightList.country !== gStorage.stats.country.id);
	}

	function liferAddLeft() {
		gStorage.stats.setLeftList({...gStorage.stats.leftList, tab: TAB_LIFER, list: gStorage.stats.obsList.slice()});
	}

	function liferAddRightEnabled() {
		return gStorage.stats.obsList && gStorage.stats.obsList.length !== 0 &&
			(gStorage.stats.leftList.tab !== TAB_LIFER ||
				gStorage.stats.leftList.country !== gStorage.stats.country.id);
	}

	function liferAddRight() {
		gStorage.stats.setRightList({...gStorage.stats.rightList, tab: TAB_LIFER, list: gStorage.stats.obsList.slice()});
	}

	function liferRxWs(status, msg) {
		if (status !== COMM_OK) {
			alert('liferRxWs: error ' + status);
			return;
		} else if (msg.result !== 'OK') {
			alert('liferRxWs: ' + msg.data.desc + '!');
			return;
		}

		if (gStorage.stats.tab !== TAB_SPEC || gStorage.stats.spec_tab !== TAB_LIFER)
			return;

// 		console.log('liferRxWs', msg);
		gStorage.stats.setLiferOrder(LSO_COUNT, LSD_DESC);
		gStorage.stats.setObsList(msg.data.list);
	}

	function liferLoadList() {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'lifer',
			op: 'list',
			data: {
				country: gStorage.stats.country.id
			},
		}

// 		console.log('liferLoadList: Tx:', msg);
		gStorage.conn.ws.send(msg, liferRxWs);
	}
	
	const liferTabChange = (e, value) => {
		gStorage.stats.setLiferTab(value);
	}

	if (gStorage.stats.tab !== TAB_SPEC || gStorage.stats.spec_tab !== TAB_LIFER)
		return null;

	if (!gStorage.stats.obsList && gStorage.conn.online)
		liferLoadList();

	return (
		<div>
			<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
				<Grid size={{xs: 12}} align={'center'}>
					<StatsFilter 
						loc = {{country: {
								value: gStorage.stats.country,
								style: {width: 200}
							}}
						}
						compare = {{
							leftIsEnabled: liferAddLeftEnabled(),
							leftAdd: liferAddLeft,
							rightIsEnabled: liferAddRightEnabled(),
							rightAdd: liferAddRight,
							pos: 'country'
						}}
					/>
				</Grid>

				<Grid size={{xs: 12}} align={'center'}>
					<Tabs style={{width: 300}}
						id='lifertab'
						value={gStorage.stats.lifer_tab}
						onChange={liferTabChange} 
						align={'center'}
						indicatorColor="primary"
						textColor="primary"
						variant="scrollable"
						scrollButtons="auto">
						<Tab
							value={LT_LIST}
							label={t('stats.life_list')}
							disabled={!gStorage.stats.obsList}/>
						<Tab
							value={LT_BY_CITY}
							label={t('stats.life_by_city')}
							disabled={!gStorage.stats.obsList}/>
						<Tab
							value={LT_BY_MONTH}
							label={t('stats.life_by_month')}
							disabled={!gStorage.stats.obsList}/>
					</Tabs>
				</Grid>
			</Grid>

			<LiferList />
			<LiferByCity />
			<LiferByMonth />
		</div>
	);
});

export default StatsLifer;
